import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text, Skeleton } from '../../../../components';
import { PULSE_NET_COLORS } from '../../../../theme/tokens';
import { useMountAnimation } from '../../../../hooks/useMountAnimation';
import type { PulseNetRow } from '../../../../mock-data/types';
import { SectionCard } from '../SectionCard';
import {
  RankPanel,
  RankLeft,
  RankLabel,
  RankNum,
  RankTop,
  Rows,
  Row,
  RowHead,
  RowLabel,
  RowVals,
  RowStrong,
  Bar,
  BarFill,
  NotAvailable,
} from './VsNetwork.styles';

export type VsNetworkProps = {
  brands: number;
  rank: number;
  top: string;
  rows: PulseNetRow[];
  /** No cross-brand benchmark API yet: render a "not available yet" placeholder for
   *  the whole comparison (rank + bars). */
  missing?: boolean;
  /** While true, the rank + per-metric bars show placeholders (the endpoint is loading). */
  loading?: boolean;
};

/**
 * The live body — the rank figure plus the You-vs-Median bars, whose fills grow
 * in on mount. Mounted only once the data is in (gated below) and owns its own
 * `useMountAnimation`, so the grow-in replays on reveal instead of running unseen
 * behind a skeleton.
 */
const NetworkBody = ({ rank, top, rows }: { rank: number; top: string; rows: PulseNetRow[] }): React.ReactElement => {
  const { t } = useTranslation();
  const mounted = useMountAnimation();

  // The comparison-metric names are a fixed UI vocabulary, translated here keyed
  // off their stable English source.
  const ROW_LABELS: Record<string, string> = {
    Retention: t('Retention'),
    'Marketing ROI': t('Marketing ROI'),
    'Player LTV': t('Player LTV'),
  };

  return (
    <React.Fragment>
      <RankPanel>
        <RankLeft>
          <RankLabel text="Rank" tag="span" />
          <RankNum>#{rank}</RankNum>
        </RankLeft>
        <RankTop text={top} tag="span" />
      </RankPanel>

      <Rows>
        {rows.map((r) => (
          <Row key={r.id}>
            <RowHead>
              <RowLabel text={ROW_LABELS[r.label] ?? r.label} tag="span" />
              <RowVals>
                <Text text="You" tag="span" /> <RowStrong>{r.you}</RowStrong> • <Text text="Median" tag="span" /> {r.median}
              </RowVals>
            </RowHead>
            <Bar>
              <BarFill $color={PULSE_NET_COLORS[r.id]} $width={mounted ? r.youPct : 0} />
            </Bar>
          </Row>
        ))}
      </Rows>
    </React.Fragment>
  );
};

/** The loading stand-in: the same rank panel + per-metric rows with the figures
 *  and bar fills replaced by placeholders. Labels stay (static chrome). */
const NetworkSkeleton = ({ rows }: { rows: PulseNetRow[] }): React.ReactElement => {
  const { t } = useTranslation();

  // The comparison-metric names are a fixed UI vocabulary, translated here keyed
  // off their stable English source (the same labels render in the live body).
  const ROW_LABELS: Record<string, string> = {
    Retention: t('Retention'),
    'Marketing ROI': t('Marketing ROI'),
    'Player LTV': t('Player LTV'),
  };

  return (
  <React.Fragment>
    <RankPanel>
      <RankLeft>
        <RankLabel text="Rank" tag="span" />
        <RankNum>
          <Skeleton width="3ch" height="0.72em" radius={6} />
        </RankNum>
      </RankLeft>
      <Skeleton width={92} height={16} radius={6} />
    </RankPanel>

    <Rows>
      {rows.map((r) => (
        <Row key={r.id}>
          <RowHead>
            <RowLabel text={ROW_LABELS[r.label] ?? r.label} tag="span" />
            <Skeleton width={120} height={14} radius={6} />
          </RowHead>
          <Skeleton block height={8} radius={4} />
        </Row>
      ))}
    </Rows>
  </React.Fragment>
  );
};

/**
 * "VS. Network" — where this brand ranks against similar white-labels: a rank
 * panel (Rank / #14 · Top 28%) plus per-metric You-vs-Median bars, each in its
 * own tinted panel with the fill growing in on mount.
 */
export const VsNetwork = ({ brands, rank, top, rows, missing = false, loading = false }: VsNetworkProps): React.ReactElement => {
  const { t } = useTranslation();

  // No benchmark source → drop the "• N Similar Brands" suffix (there is no count)
  // and replace the whole body with a muted "not available yet" note.
  if (missing) {
    return (
      <SectionCard title={t('VS. Whitelabels.com Network')}>
        <NotAvailable text="Benchmarks across similar brands aren't available yet" tag="span" />
      </SectionCard>
    );
  }

  return (
    <SectionCard title={t('VS. Whitelabels.com Network')} suffix={t('• {{count}} Similar Brands')} suffixPlaceholders={{ count: brands }}>
      {loading ? <NetworkSkeleton rows={rows} /> : <NetworkBody rank={rank} top={top} rows={rows} />}
    </SectionCard>
  );
};

export default VsNetwork;
